import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchSpaceContributions,
  fetchSpaceContribution,
  createSpaceContribution,
  updateSpaceContribution,
  deleteSpaceContribution,
} from '@/lib/api/space-contributions'
import type { SpaceContributionFormData, SpaceContributionFilters } from '@/types/space-contributions'
import { toast } from 'sonner'

/**
 * Query key factory for space contributions
 * Provides consistent keys for cache management
 */
export const spaceContributionKeys = {
  all: ['space-contributions'] as const,
  lists: () => [...spaceContributionKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: SpaceContributionFilters) =>
    [...spaceContributionKeys.lists(), organizationId, filters] as const,
  details: () => [...spaceContributionKeys.all, 'detail'] as const,
  detail: (id: string) => [...spaceContributionKeys.details(), id] as const,
}

/**
 * Fetch all space contributions for current organization with optional filtering
 */
export function useSpaceContributions(filters?: SpaceContributionFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: spaceContributionKeys.list(organizationId || '', filters),
    queryFn: () => fetchSpaceContributions(organizationId!, filters),
    enabled: !!organizationId,
  })
}

/**
 * Fetch a single space contribution by ID
 */
export function useSpaceContribution(id: string | undefined) {
  return useQuery({
    queryKey: spaceContributionKeys.detail(id || ''),
    queryFn: () => fetchSpaceContribution(id!),
    enabled: !!id,
  })
}

/**
 * Create a new space contribution
 */
export function useCreateSpaceContribution() {
  const queryClient = useQueryClient()
  const { currentOrganization, currentLocation } = useTenant()

  return useMutation({
    mutationFn: (data: SpaceContributionFormData) =>
      createSpaceContribution({
        ...data,
        organization_id: currentOrganization!.id,
        location_id: currentLocation!.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceContributionKeys.lists() })
      toast.success('Space contribution created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create space contribution', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing space contribution
 */
export function useUpdateSpaceContribution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<SpaceContributionFormData>
    }) => updateSpaceContribution(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: spaceContributionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: spaceContributionKeys.detail(id) })
      toast.success('Space contribution updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update space contribution', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a space contribution (soft delete)
 */
export function useDeleteSpaceContribution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteSpaceContribution(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceContributionKeys.lists() })
      toast.success('Space contribution deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete space contribution', {
        description: error.message,
      })
    },
  })
}
